import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface PoetrySectionProps {
  language: 'de' | 'ru';
}

interface Poem {
  id: number;
  title: { de: string; ru: string };
  author: { de: string; ru: string };
  text: { de: string; ru: string };
  theme: { de: string; ru: string };
  year: string;
}

export default function PoetrySection({ language }: PoetrySectionProps) {
  const sectionAnim = useScrollAnimation();
  const [selectedPoem, setSelectedPoem] = useState<Poem | null>(null);

  const content = {
    de: {
      title: 'Poesie & Philosophie',
      subtitle: 'Verse, die die Seele berühren',
      close: 'Schließen',
      theme: 'Thema'
    },
    ru: {
      title: 'Поэзия и философия',
      subtitle: 'Стихи, которые трогают душу',
      close: 'Закрыть',
      theme: 'Тема'
    }
  };

  const poems: Poem[] = [
    {
      id: 1,
      title: { 
        de: 'Wanderers Nachtlied', 
        ru: 'Ночная песнь странника' 
      },
      author: { 
        de: 'Johann Wolfgang von Goethe', 
        ru: 'Иоганн Вольфганг фон Гёте' 
      },
      text: {
        de: 'Über allen Gipfeln\nIst Ruh,\nIn allen Wipfeln\nSpürest du\nKaum einen Hauch;\nDie Vögelein schweigen im Walde.\nWarte nur, balde\nRuhest du auch.',
        ru: 'Над всеми вершинами\nПокой,\nВо всех верхушках\nТы чувствуешь\nЕдва дыхание;\nПтички молчат в лесу.\nПодожди, скоро\nУспокоишься и ты.'
      },
      theme: { de: 'Natur & Ruhe', ru: 'Природа и покой' },
      year: '1780'
    },
    {
      id: 2,
      title: { 
        de: 'An die Freude', 
        ru: 'К радости' 
      },
      author: { 
        de: 'Friedrich Schiller', 
        ru: 'Фридрих Шиллер' 
      },
      text: {
        de: 'Freude, schöner Götterfunken,\nTochter aus Elysium,\nWir betreten feuertrunken,\nHimmlische, dein Heiligtum!\nDeine Zauber binden wieder,\nWas die Mode streng geteilt;\nAlle Menschen werden Brüder,\nWo dein sanfter Flügel weilt.',
        ru: 'Радость, прекрасная искра богов,\nДочь из Элизиума,\nМы входим опьяненные огнем,\nНебесная, в твое святилище!\nТвои чары снова связывают,\nЧто мода строго разделила;\nВсе люди становятся братьями,\nГде твое нежное крыло пребывает.'
      },
      theme: { de: 'Freude & Brüderlichkeit', ru: 'Радость и братство' },
      year: '1785'
    },
    {
      id: 3,
      title: { 
        de: 'Lorelei', 
        ru: 'Лорелея' 
      },
      author: { 
        de: 'Heinrich Heine', 
        ru: 'Генрих Гейне' 
      },
      text: {
        de: 'Ich weiß nicht, was soll es bedeuten,\nDass ich so traurig bin;\nEin Märchen aus alten Zeiten,\nDas kommt mir nicht aus dem Sinn.\nDie Luft ist kühl und es dunkelt,\nUnd ruhig fließt der Rhein;\nDer Gipfel des Berges funkelt\nIm Abendsonnenschein.',
        ru: 'Не знаю, что это значит,\nЧто я так печален;\nСказка из старых времен,\nКоторая не выходит из головы.\nВоздух прохладен и темнеет,\nИ спокойно течет Рейн;\nВершина горы сверкает\nВ вечернем солнечном свете.'
      },
      theme: { de: 'Sehnsucht & Mythos', ru: 'Тоска и миф' },
      year: '1824'
    },
    {
      id: 4,
      title: { 
        de: 'Ich liebte dich', 
        ru: 'Я вас любил' 
      },
      author: { 
        de: 'Alexander Puschkin', 
        ru: 'Александр Пушкин' 
      },
      text: {
        de: 'Ich liebte dich, und diese Liebe noch,\nVielleicht, ist nicht erloschen in der Seele;\nDoch soll sie dich nicht weiter mehr betrüben;\nIch will dich nicht betrüben irgendwie.\nIch liebte dich so hoffnungslos, so zärtlich,\nSo aufrichtig, so innig wie kein andrer:\nGott gebe, dass ein andrer dich so liebe,\nWie ich dich einst geliebt!',
        ru: 'Я вас любил: любовь еще, быть может,\nВ душе моей угасла не совсем;\nНо пусть она вас больше не тревожит;\nЯ не хочу печалить вас ничем.\nЯ вас любил безмолвно, безнадежно,\nТо робостью, то ревностью томим;\nЯ вас любил так искренно, так нежно,\nКак дай вам бог любимой быть другим.'
      },
      theme: { de: 'Liebe & Abschied', ru: 'Любовь и прощание' },
      year: '1829'
    },
    {
      id: 5,
      title: { 
        de: 'Mondnacht', 
        ru: 'Лунная ночь' 
      },
      author: { 
        de: 'Joseph von Eichendorff', 
        ru: 'Йозеф фон Эйхендорф' 
      },
      text: {
        de: 'Es war, als hätt der Himmel\nDie Erde still geküsst,\nDass sie im Blütenschimmer\nVon ihm nun träumen müsst.\nDie Luft ging durch die Felder,\nDie Ähren wogten sacht,\nEs rauschten leis die Wälder,\nSo sternklar war die Nacht.',
        ru: 'Казалось, будто небо\nТихо поцеловало землю,\nЧтобы она в цветочном сиянии\nТеперь должна была мечтать о нем.\nВоздух шел через поля,\nКолосья тихо колыхались,\nЛеса тихо шумели,\nТак звездно ясна была ночь.'
      },
      theme: { de: 'Romantik & Natur', ru: 'Романтика и природа' },
      year: '1837'
    },
    {
      id: 6,
      title: { 
        de: 'Herbsttag', 
        ru: 'Осенний день' 
      },
      author: { 
        de: 'Rainer Maria Rilke', 
        ru: 'Райнер Мария Рильке' 
      },
      text: {
        de: 'Herr: es ist Zeit. Der Sommer war sehr groß.\nLeg deinen Schatten auf die Sonnenuhren,\nund auf den Fluren lass die Winde los.\nBefiehl den letzten Früchten voll zu sein;\ngib ihnen noch zwei südlichere Tage,\ndränge sie zur Vollendung hin und jage\ndie letzte Süße in den schweren Wein.',
        ru: 'Господи: пора. Лето было очень большим.\nПоложи свою тень на солнечные часы,\nи на полях отпусти ветры.\nПрикажи последним плодам быть полными;\nдай им еще два более южных дня,\nподтолкни их к завершению и загони\nпоследнюю сладость в тяжелое вино.'
      },
      theme: { de: 'Vergänglichkeit', ru: 'Бренность' },
      year: '1902'
    },
    {
      id: 7,
      title: { 
        de: 'Alles ist geplündert', 
        ru: 'Все расхищено' 
      },
      author: { 
        de: 'Anna Achmatowa', 
        ru: 'Анна Ахматова' 
      },
      text: {
        de: 'Alles ist geplündert, verraten, verkauft,\nDer schwarze Tod hat seine Flügel ausgebreitet,\nAlles ist vor Hunger ausgehungert,\nWarum ist uns dann so hell?\nBei Tag duftet die Kirschblüte\nIn der Nähe der Stadt auf wundersame Weise,\nIn der Nacht leuchten neue Sternbilder\nIn den Tiefen des durchsichtigen Juli.',
        ru: 'Все расхищено, предано, продано,\nЧерной смерти мелькало крыло,\nВсе голодной тоскою изглодано,\nОтчего же нам стало светло?\nДнем дыханьем веет вишневым\nНепонятно откуда взятым,\nНочью блещут созвездья новые\nВ глубине прозрачных июльских.'
      },
      theme: { de: 'Hoffnung & Leid', ru: 'Надежда и страдание' },
      year: '1921'
    },
    {
      id: 8,
      title: { 
        de: 'Sei still', 
        ru: 'Молчи, скрывайся' 
      },
      author: { 
        de: 'Fjodor Tjutschew', 
        ru: 'Федор Тютчев' 
      },
      text: {
        de: 'Sei still, verbirg dich und verhehle\nDeine Gefühle und Träume –\nLass sie in der Tiefe der Seele\nAufgehen und untergehen still\nWie Sterne in der Nacht:\nBetrachte sie – und schweige.\nWie kann das Herz sich äußern?\nWie kann ein andrer dich verstehen?',
        ru: 'Молчи, скрывайся и таи\nИ чувства и мечты свои –\nПускай в душевной глубине\nВстают и заходят оне\nБезмолвно, как звезды в ночи, –\nЛюбуйся ими – и молчи.\nКак сердцу высказать себя?\nДругому как понять тебя?'
      },
      theme: { de: 'Innerlichkeit', ru: 'Внутренний мир' },
      year: '1830'
    },
    {
      id: 9,
      title: { 
        de: 'Ich bereue nichts', 
        ru: 'Не жалею, не зову' 
      },
      author: { 
        de: 'Sergei Jessenin', 
        ru: 'Сергей Есенин' 
      },
      text: {
        de: 'Ich bereue nichts, ich rufe nichts zurück, ich weine nicht,\nAlles wird vergehen, wie weißer Apfelrauch.\nVom Gold der Welke überwältigt,\nWerde ich nicht mehr jung sein.\nDu, mein Herz, was hast du dich aufgeregt?\nWas beunruhigt dich so sehr?\nWas ist geschehen, was ist vergangen,\nKommt nicht wieder zurück.',
        ru: 'Не жалею, не зову, не плачу,\nВсе пройдет, как с белых яблонь дым.\nУвяданья золотом охваченный,\nЯ не буду больше молодым.\nТы теперь не так уж будешь биться,\nСердце, тронутое холодком,\nИ страна березового ситца\nНе заманит шляться босиком.'
      },
      theme: { de: 'Vergänglichkeit', ru: 'Бренность' },
      year: '1921'
    },
    {
      id: 10,
      title: { 
        de: 'Hymnen an die Nacht', 
        ru: 'Гимны к ночи' 
      },
      author: { 
        de: 'Novalis', 
        ru: 'Новалис' 
      },
      text: {
        de: 'Welcher Lebendige, Sinnbegabte,\nLiebt nicht vor allen Wundererscheinungen\nDes verbreiteten Raums um ihn,\nDas allerfreuliche Licht –\nMit seinen Farben, seinen Strahlen und Wogen;\nSeiner milden Allgegenwart, als weckender Tag.\nWie des Lebens innerste Seele atmet es\nDer rastlosen Gestirne Riesenwelt.',
        ru: 'Какой живой, одаренный чувствами,\nНе любит больше всех чудесных явлений\nРаспространенного пространства вокруг него,\nСамый радостный свет –\nС его цветами, его лучами и волнами;\nЕго мягким вездесущием, как пробуждающий день.\nКак внутренняя душа жизни дышит он\nГигантским миром неутомимых светил.'
      },
      theme: { de: 'Mystik & Nacht', ru: 'Мистика и ночь' },
      year: '1800'
    },
    {
      id: 11,
      title: { 
        de: 'Er ists', 
        ru: 'Это он' 
      },
      author: { 
        de: 'Eduard Mörike', 
        ru: 'Эдуард Мёрике' 
      },
      text: {
        de: 'Frühling lässt sein blaues Band\nWieder flattern durch die Lüfte;\nSüße, wohlbekannte Düfte\nStreifen ahnungsvoll das Land.\nVeilchen träumen schon,\nWollen balde kommen.\nHorch, von fern ein leiser Harfenton!\nFrühling, ja du bists!\nDich hab ich vernommen!',
        ru: 'Весна снова развевает\nСвою голубую ленту в воздухе;\nСладкие, хорошо знакомые ароматы\nПредчувственно касаются земли.\nФиалки уже мечтают,\nХотят скоро прийти.\nСлушай, издалека тихий звук арфы!\nВесна, да это ты!\nТебя я услышал!'
      },
      theme: { de: 'Frühling & Erwachen', ru: 'Весна и пробуждение' },
      year: '1829'
    },
    {
      id: 12,
      title: { 
        de: 'Ich möchte mit dir leben', 
        ru: 'Я хотела бы жить с тобой' 
      },
      author: { 
        de: 'Marina Zwetajewa', 
        ru: 'Марина Цветаева' 
      },
      text: {
        de: 'Ich möchte mit dir leben\nIn einer kleinen Stadt\nMit ewigen Glocken\nUnd ewigem Glockengeläut.\nUnd in diesem kleinen Städtchen –\nEin altes Gasthaus –\nWo das dünne Klingeln\nDer alten Uhr – wie die Zeit tropft.',
        ru: 'Я хотела бы жить с тобой\nВ маленьком городе,\nГде вечные колокола\nИ вечный звон колоколов.\nИ в этом маленьком городке –\nСтарая гостиница –\nГде тонкий звон\nСтарых часов – как капает время.'
      },
      theme: { de: 'Liebe & Sehnsucht', ru: 'Любовь и тоска' },
      year: '1916'
    }
  ];

  const t = content[language];

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 30 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' } 
    }
  };

  return (
    <section className="py-24 bg-gradient-to-b from-[#F5F5DC] to-[#E8E8D0]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div 
          ref={sectionAnim.ref}
          initial="hidden"
          animate={sectionAnim.isVisible ? "visible" : "hidden"}
          variants={fadeInUp}
          className="text-center mb-16"
        >
          <h2 className="font-serif text-5xl text-[#1B3A5F] mb-4">
            {t.title}
          </h2>
          <div className="w-32 h-1 bg-gradient-to-r from-transparent via-[#C5B358] to-transparent mx-auto mb-6"></div>
          <p className="text-lg text-[#1B3A5F]/80 max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </motion.div>

        <motion.div 
          initial="hidden"
          animate={sectionAnim.isVisible ? "visible" : "hidden"}
          variants={staggerContainer}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {poems.map((poem) => (
            <motion.div
              key={poem.id}
              variants={cardVariants}
              onClick={() => setSelectedPoem(poem)}
              className="bg-white/70 backdrop-blur-sm rounded-lg p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-[#C5B358]/30 hover:border-[#8B1538] cursor-pointer group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="font-serif text-xl text-[#1B3A5F] mb-2 group-hover:text-[#8B1538] transition-colors">
                    {poem.title[language]}
                  </h3>
                  <p className="text-sm text-[#1B3A5F]/70 mb-1">
                    {poem.author[language]}
                  </p>
                  <p className="text-xs text-[#C5B358] font-medium">
                    {poem.year}
                  </p>
                </div>
                <i className="ri-book-open-line text-2xl text-[#8B1538] group-hover:scale-110 transition-transform"></i>
              </div>

              <div className="mb-4">
                <span className="inline-block px-3 py-1 bg-[#C5B358]/20 text-[#1B3A5F] text-xs rounded-full font-medium">
                  {poem.theme[language]}
                </span>
              </div>

              <p className="font-serif text-sm text-[#1B3A5F]/80 leading-relaxed line-clamp-4 whitespace-pre-line">
                {poem.text[language]}
              </p>

              <div className="mt-4 flex items-center text-[#8B1538] text-sm font-medium group-hover:translate-x-2 transition-transform">
                <span>{t.readMore}</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Poem Modal */}
      <AnimatePresence>
        {selectedPoem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedPoem(null)}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-[#F5F5DC] to-[#E8E8D0] rounded-lg p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl border-2 border-[#C5B358]"
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="font-serif text-3xl text-[#1B3A5F] mb-2">
                    {selectedPoem.title[language]}
                  </h3>
                  <p className="text-lg text-[#1B3A5F]/80 mb-1">
                    {selectedPoem.author[language]}
                  </p>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-sm text-[#C5B358] font-medium">
                      {selectedPoem.year}
                    </span>
                    <span className="inline-block px-3 py-1 bg-[#C5B358]/20 text-[#1B3A5F] text-xs rounded-full font-medium">
                      {selectedPoem.theme[language]}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedPoem(null)}
                  className="text-[#1B3A5F] hover:text-[#8B1538] transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-3xl"></i>
                </button>
              </div>

              <div className="h-px bg-gradient-to-r from-transparent via-[#C5B358] to-transparent mb-6"></div>

              <div className="bg-white/60 rounded-lg p-6 shadow-inner">
                <i className="ri-double-quotes-l text-4xl text-[#8B1538] mb-4 block"></i>
                <p className="font-serif text-lg text-[#1B3A5F] leading-relaxed whitespace-pre-line">
                  {selectedPoem.text[language]}
                </p>
                <i className="ri-double-quotes-r text-4xl text-[#8B1538] mt-4 block text-right"></i>
              </div>

              <button
                onClick={() => setSelectedPoem(null)}
                className="mt-6 w-full bg-gradient-to-r from-[#8B1538] to-[#6B0F2A] text-white py-3 rounded-md font-serif text-lg hover:shadow-lg transition-all duration-300 border-2 border-[#C5B358] whitespace-nowrap cursor-pointer"
              >
                {t.close}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
